import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { ThemeProvider, createGlobalStyle } from 'styled-components'

import { readStoredThemeMode, storeThemeMode } from './themeStorage'
import { RedesignThemeContextValue, ResolvedTheme, ThemeMode } from './types'
import { theme } from '../constants'

const DARK_MODE_QUERY = '(prefers-color-scheme: dark)'

const ThemeVariables = createGlobalStyle<{ $resolvedTheme: ResolvedTheme }>`
  [data-redesign-theme] {
    color-scheme: ${({ $resolvedTheme }) => $resolvedTheme};
    --redesign-text-primary: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#F7F7FB' : '#0A0A18'};
    --redesign-text-secondary: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#C5C7D2' : '#54576B'};
    --redesign-text-muted: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#A5A8B8' : '#74778B'};
    --redesign-background-canvas: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#151620' : '#F7F8FA'};
    --redesign-background-subtle: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#1A1C27' : '#FBFCFD'};
    --redesign-background-muted: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#242633' : '#F0F2F5'};
    --redesign-surface: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#1E202B' : '#FFFFFF'};
    --redesign-surface-elevated: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#282A37' : '#FFFFFF'};
    --redesign-surface-editorial: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#242633' : '#171824'};
    --redesign-student-soft: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#FFF8D9' : '#FFF1B3'};
    --redesign-border: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#3B3E4D' : '#DDE1E7'};
    --redesign-border-strong: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark' ? '#5B5F70' : '#B9C0CB'};
    --redesign-focus: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark'
        ? 'rgba(104, 211, 238, 0.62)'
        : 'rgba(8, 168, 210, 0.34)'};
    --redesign-overlay: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark'
        ? 'rgba(0, 0, 0, 0.66)'
        : 'rgba(10, 10, 24, 0.42)'};
    --redesign-shadow: ${({ $resolvedTheme }) =>
      $resolvedTheme === 'dark'
        ? 'rgba(0, 0, 0, 0.48)'
        : 'rgba(10, 10, 24, 0.12)'};
  }
`

const RedesignThemeContext = createContext<RedesignThemeContextValue | null>(
  null,
)

const getSystemTheme = (): ResolvedTheme =>
  typeof window !== 'undefined' && window.matchMedia(DARK_MODE_QUERY).matches
    ? 'dark'
    : 'light'

export const RedesignThemeProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredThemeMode)
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme)

  useEffect(() => {
    const mediaQuery = window.matchMedia(DARK_MODE_QUERY)
    const onChange = (event: MediaQueryListEvent) =>
      setSystemTheme(event.matches ? 'dark' : 'light')

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light')
    mediaQuery.addEventListener?.('change', onChange)
    return () => mediaQuery.removeEventListener?.('change', onChange)
  }, [])

  const setMode = (nextMode: ThemeMode) => {
    setModeState(nextMode)
    storeThemeMode(nextMode)
  }

  const resolvedTheme = mode === 'system' ? systemTheme : mode
  const contextValue = useMemo(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme],
  )

  return (
    <RedesignThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <ThemeVariables $resolvedTheme={resolvedTheme} />
        {children}
      </ThemeProvider>
    </RedesignThemeContext.Provider>
  )
}

export const useRedesignTheme = () => {
  const context = useContext(RedesignThemeContext)

  if (!context) {
    throw new Error(
      'useRedesignTheme must be used within RedesignThemeProvider',
    )
  }

  return context
}
